import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppShell } from '../components/layout/AppShell'
import { orgService } from '../services/orgService'
import type { Race } from '../types/index'

export function OrgDashboardPage() {
  const navigate = useNavigate()
  const [races, setRaces] = useState<Race[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    orgService.getRaces()
      .then(setRaces)
      .catch(() => setError('Failed to load races'))
      .finally(() => setLoading(false))
  }, [])

  const statusBadge = (status: Race['status']) => {
    const map: Record<Race['status'], { label: string; cls: string }> = {
      upcoming:  { label: 'Upcoming',  cls: 'bg-yellow-100 text-yellow-800' },
      active:    { label: 'Active',    cls: 'bg-green-100 text-green-800' },
      completed: { label: 'Completed', cls: 'bg-blue-100 text-blue-800' },
      archived:  { label: 'Archived',  cls: 'bg-gray-100 text-gray-500' },
    }
    const { label, cls } = map[status] ?? map.archived
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
  }

  return (
    <AppShell>
      <div className="h-full overflow-y-auto p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
            </svg>
            Race Organizer
          </h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
            {error}
          </div>
        )}

        {/* Race list */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading races...</div>
        ) : races.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-lg">No races have been assigned yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {races.map(race => (
              <div
                key={race.id}
                onClick={() => navigate(`/org/races/${race.id}`)}
                className="bg-white rounded-lg border border-gray-200 px-4 py-3 cursor-pointer hover:border-indigo-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{race.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {race.eventDate ? new Date(race.eventDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'No date set'}
                      {race.teamCount !== undefined && <span className="ml-2">· {race.teamCount} team{race.teamCount !== 1 ? 's' : ''}</span>}
                    </p>
                    {race.slug && (
                      <p className="text-xs font-mono text-indigo-500 mt-0.5">/watch/{race.slug}/BIB</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(race.status)}
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  )
}
